import type { JSX, PropsWithChildren } from 'react';
import { Root as PopoverRoot } from '@radix-ui/react-popover';
import { ColorPickerProvider } from './ColorPickerContext';
import {
  DEFAULT_COLOR_FOR_COMPARISON,
  DEFAULT_MIN_CONTRAST,
  DEFAULT_OPACITY_FOR_CONTRAST_CALCULATION,
} from './consts';

export type ColorPickerProps = PropsWithChildren<{
  /**
   * The color to compare against when checking contrast ratio.
   */
  colorForComparison?: string;
  /**
   * The minimum contrast ratio to enforce when `shouldEnforceMinContrast` is true.
   */
  minimumContrastRatio?: number;
  /**
   * The opacity to apply to the value color when calculating contrast.
   */
  opacityForContrastCalculation?: number;
  /**
   * Callback function invoked when the color value changes.
   */
  onValueChange: (value: string) => void;
  /**
   * The current color value in hex format (e.g., "#ff0000").
   */
  value: string;
}>;

/**
 * A composable color picker built from low-level building blocks. Choose the
 * parts you need, leave out the ones you don't, and include any custom UI
 * alongside its subcomponents.
 *
 * For most use cases in the Wistia app, prefer the pre-composed ColorPicker
 * at `~/components/ColorPicker`, which assembles these primitives with
 * sensible defaults (brand colors, contrast controls, popover/inline modes).
 */
export const ColorPicker = ({
  children,
  colorForComparison = DEFAULT_COLOR_FOR_COMPARISON,
  minimumContrastRatio = DEFAULT_MIN_CONTRAST,
  opacityForContrastCalculation = DEFAULT_OPACITY_FOR_CONTRAST_CALCULATION,
  onValueChange,
  value,
}: ColorPickerProps): JSX.Element => {
  return (
    <PopoverRoot modal={false}>
      <ColorPickerProvider
        colorForComparison={colorForComparison}
        minimumContrastRatio={minimumContrastRatio}
        onValueChange={onValueChange}
        opacityForContrastCalculation={opacityForContrastCalculation}
        value={value.toLocaleLowerCase()}
      >
        {children}
      </ColorPickerProvider>
    </PopoverRoot>
  );
};

ColorPicker.displayName = 'ColorPicker';
